/* eslint-disable @typescript-eslint/no-explicit-any */
import BlogList from '@/components/BlogList'
import ButtonLink from '@/components/ButtonLink'
import Badge from '@/components/Checkout/Badge'
import { CheckoutEmpty } from '@/components/Icon/CheckoutEmpty'
import { LoadingIndicator } from '@/components/LoadingIndicator'
import PageBreadcrumb from '@/components/PageBreadcrumb'
import ModalConfirmChangeRegion from '@/components/Popup/ModalConfirmChangeRegion'
import ModalNotAvailable from '@/components/Popup/ModalNotAvailable'
import Recommend from '@/components/Recommend'
import {
  AddToCartType,
  PAYMENT_METHODS,
  SHIPPING_METHOD,
  TIMES,
} from '@/constants'
import { useAuth, useCart, useNotification, useStore } from '@/containers'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import type { GuestInfo, Promotion } from '@/types'
import { type CartItemType, type OrderInput } from '@/types'
import { api } from '@/utils'
import { getTranslate, setContext } from '@/utils/api-interceptors'
import {
  getBannerSSR,
  getBottomMenu,
  getConfigSSR,
  getHeaderMenu,
  getKeyword,
  getProductForYou,
  getProvinces,
  getSaleRegion,
} from '@/utils/endpoint'
import * as pixel from '@/utils/fpixel'
import { translateSchema } from '@/utils/validations'
import { sendGAEvent, sendGTMEvent } from '@next/third-parties/google'
import { getCookies } from 'cookies-next'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
const DeliveryAddress = dynamic(
  () => import('@/components/Checkout/DeliveryAddress'),
  {
    ssr: false,
  }
)
const CartItem = dynamic(() => import('@/components/Checkout/CartItem'), {
  ssr: false,
})
const CartItems = dynamic(() => import('@/components/Checkout/CartItems'), {
  ssr: false,
})
const ErrorPopup = dynamic(() => import('@/components/Popup/ErrorPopup'), {
  ssr: false,
})
interface CartPops {
  query?: {
    type: string
    id: string
  }
  productForYou: Promotion
}
// eslint-disable-next-line @typescript-eslint/no-var-requires
const customParseFormat = require('dayjs/plugin/customParseFormat')
dayjs.extend(customParseFormat)

const CheckoutCart = ({ query, productForYou }: CartPops) => {
  const t = useTranslations('Cart')
  const tCheckout = useTranslations('CheckoutPayment')
  const breadcrumbs = [{ label: t('cart'), href: '/checkout/cart' }]
  const {
    cart,
    userAddress,
    user,
    fetchCart,
    fetchAddress,
    setDestinationCode,
    listBranch,
    storeSelected,
    getListStore,
    getListBranch,
    errorAuth,
    clientIP,
    listAddress,
    setVoucherApply,
  } = useAuth()
  const { systemConfig } = useNotification()
  const {
    changeQuantityItem,
    cartLoading,
    setGuestInfo,
    guestInfo,
    couponApply,
    setCouponApply,
    setIsPreOrder,
    isInstallment,
    setIsInstallment,
  } = useCart()
  const {
    provinces,
    provinceAvailable,
    districts,
    wards,
    getDistricts,
    getWards,
    setWards,
    regionChange,
    currentRegion,
    isChangeRegionModal,
    setIsChangeRegionModal,
    setSaleRegion,
    addressVersion,
    setAddressVersion,
  } = useStore()
  const router = useRouter()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [errorCODE, setErrorCODE] = useState('')
  const [currentTime] = useState<number>(dayjs(dayjs().hour()).valueOf())
  const [bonusDay, setBonusDay] = useState<number>(0)
  const [preOrderTime, setPreOrderTime] = useState<string>('')
  const [preOrderDate, setPreOrderDate] = useState<number>(0)
  const [openErrorPopup, setOpenErrorPopup] = useState(false)

  const [openModalCartNotAvailable, setOpenModalCartNotAvailable] =
    useState(false)
  const [refetch, setRefetch] = useState(false)
  const [useBonusState, setUseBonusState] = useState(false)
  const cartType = () => {
    switch (router?.query?.type) {
      case AddToCartType.PRE_ORDER:
        return AddToCartType.PRE_ORDER
      case AddToCartType.INSTALLMENT:
        return AddToCartType.INSTALLMENT
      default:
        return AddToCartType.ADD
    }
  }

  const getFetchCartParams = () => {
    const params = {
      ...(couponApply.length ? { coupon: couponApply } : {}),
      shippingMethod: defaultShippingMethod,
    }

    if (router.query.type === AddToCartType.PRE_ORDER && router.query.id) {
      return {
        ...params,
        isPreOrder: true,
        productIds: router.query.id,
        action: 'cart 2',
      }
    }
    if (router.query.type === AddToCartType.INSTALLMENT && router.query.id) {
      return { ...params, productIds: `${router.query.id}:1`, action: 'cart 3' }
    }
    if (
      router.query.type !== AddToCartType.INSTALLMENT &&
      router.query.type !== AddToCartType.PRE_ORDER
    ) {
      if (router.query.shippingAddress && user && userAddress?.wardCode) {
        setDestinationCode(userAddress.wardCode)
        return { ...params, wardCode: userAddress.wardCode, action: 'cart 4' }
      }
      if (!router.query.shippingAddress && user && userAddress?.wardCode) {
        setDestinationCode(userAddress.wardCode)
        return { ...params, wardCode: userAddress.wardCode, action: 'cart 5' }
      }
    }
    return params as Record<string, any>
  }

  useEffect(() => {
    if (user || refetch) {
      fetchAddress()
      fetchCart(getFetchCartParams())
      setRefetch(false)
    }
  }, [router, user, refetch])

  useEffect(() => {
    getListStore()
    getListBranch()
    setVoucherApply([])
    fetchCart(getFetchCartParams())
    return () => {
      setIsProcessing(false)
    }
  }, [router])

  useEffect(() => {
    switch (router?.query?.type) {
      case AddToCartType.PRE_ORDER:
        setIsPreOrder(true)
        setIsInstallment(false)
        break
      case AddToCartType.INSTALLMENT:
        setIsPreOrder(false)
        setIsInstallment(true)
        break
      default:
        setIsPreOrder(false)
        setIsInstallment(false)
        break
    }
    return
  }, [router?.query?.type])

  const checkShippingMethodDefault = () => {
    const storeChange = listBranch?.find(e => e.code === router.query.pis)
    const hasWarehouseProduct = cart?.products?.some(e => e.receiveAtWarehouse)
    const hasSelectedWarehouseProduct = cart?.products?.some(
      e => e.selectedReceiveAtWarehouse
    )
    const hasServices = cart?.products?.some(e => e?.services)
    const hasServiceWithQuantity = cart?.products?.some(e =>
      e.services?.some(service => service.quantity > 0)
    )

    // Case 1: Pickup in store if warehouse product is selected and no services have quantity > 0
    if (
      (hasSelectedWarehouseProduct &&
        hasWarehouseProduct &&
        hasServices &&
        !hasServiceWithQuantity) ||
      (router.query.pis &&
        currentRegion === regionChange &&
        !hasServiceWithQuantity) ||
      (router.query.pis &&
        hasWarehouseProduct &&
        hasSelectedWarehouseProduct &&
        hasServices &&
        !hasServiceWithQuantity) ||
      (router.query.pis &&
        currentRegion === storeChange?.saleRegionCode &&
        !hasServiceWithQuantity)
    ) {
      return SHIPPING_METHOD.PICKUP_IN_STORE
    }

    // Case 2: Standard shipping if warehouse product is not selected but there are services or service with quantity > 0
    if (
      (hasWarehouseProduct &&
        !hasSelectedWarehouseProduct &&
        hasServices &&
        hasServiceWithQuantity) ||
      (hasWarehouseProduct && !hasSelectedWarehouseProduct && hasServices) ||
      cart?.products?.some(
        e => e.shippingMethod === SHIPPING_METHOD.STANDARD
      ) ||
      (cart?.shippingMethod === SHIPPING_METHOD.STANDARD && !router.query.pis)
    ) {
      return SHIPPING_METHOD.STANDARD
    }

    // Default fallback to standard shipping
    return SHIPPING_METHOD.STANDARD
  }

  const defaultShippingMethod = checkShippingMethodDefault()

  const conditionDisplayOptionShipping = cart?.products?.some(
    e => e.selectedReceiveAtWarehouse
  )
    ? false
    : true

  const onSubmitFn = () => {
    setIsSubmitted(true)
    pixel.fbqEvent('InitiateCheckout')
  }

  useEffect(() => {
    if (cart && cart?.products) {
      const dateTimestamp = dayjs(
        dayjs().add(cart?.products[0]?.preOrder?.deliveredAfter, 'day')
      ).valueOf()
      if (dateTimestamp && dateTimestamp > 0) {
        setPreOrderDate(dateTimestamp)
      }
    }
  }, [cart?.products])

  useEffect(() => {
    if (currentTime) {
      const timeAvailable = TIMES.filter(e => (e.hour as number) > currentTime)
      if (timeAvailable && timeAvailable.length > 0) {
        setPreOrderTime(timeAvailable[0]?.value)
      } else {
        setPreOrderTime(TIMES[0]?.value)
        setBonusDay(1)
      }
    }
  }, [currentTime])

  useEffect(() => {
    const provinceCode =
      user?.provinceCode || userAddress?.provinceCode || guestInfo?.provinceCode
    const districtCode =
      user?.districtCode || userAddress?.districtCode || guestInfo?.districtCode

    const isV2 = userAddress
      ? !!userAddress.useAddressV2
      : !!guestInfo?.useAddressV2

    if (provinceCode && districts.length === 0 && !isV2) {
      getDistricts(provinceCode)
    }
    if (districtCode && wards.length === 0 && !isV2) {
      getWards(districtCode)
    }
    if (provinceCode && wards.length === 0 && isV2) {
      getWards(provinceCode)
    }
  }, [user, guestInfo, userAddress, addressVersion, setAddressVersion])
  const guestInfoByRegion =
    guestInfo?.saleRegionCode == currentRegion
      ? guestInfo
      : {
          name: '',
          phone: '',
          useAddressV2: guestInfo?.useAddressV2 ?? true,
          provinceCode: '',
          districtCode: '',
          wardCode: '',
          address: '',
          gender: '',
        }
  const provinceStorage =
    typeof window !== 'undefined' ? localStorage?.getItem('province') : ''
  const formik = useFormik({
    initialValues: {
      name: user?.name || guestInfoByRegion?.name || '',
      phone: user?.phone || guestInfoByRegion?.phone || '',
      alternatePhone: userAddress?.phone || '',
      alternateName: userAddress?.name || '',
      useAddressV2:
        userAddress?.useAddressV2 ?? guestInfoByRegion?.useAddressV2 ?? true,
      provinceCode:
        userAddress?.provinceCode ||
        guestInfoByRegion?.provinceCode ||
        provinceStorage ||
        '',
      districtCode:
        userAddress?.districtCode || guestInfoByRegion?.districtCode || '',
      wardCode: userAddress?.wardCode || guestInfoByRegion?.wardCode || '',
      address: userAddress?.address || guestInfoByRegion?.address || '',
      gender: userAddress?.gender || guestInfoByRegion?.gender || 'male',
      storeCode: router.query.pis ? router.query.pis.toString() : '',
      shippingMethod: defaultShippingMethod,
      preference: false,
      vat: false,
      date: router?.query?.type === AddToCartType.PRE_ORDER ? preOrderDate : '',
      time:
        router?.query?.type === AddToCartType.PRE_ORDER && preOrderTime
          ? preOrderTime
          : '',
      note: '',
      invoiceName: '',
      invoiceAddress: '',
      invoiceTaxNumber: '',
      invoiceEmail: '',
      coupon: [],
      useBonus: useBonusState,
    },
    initialTouched: {
      phone: false,
      alternatePhone: false,
    },
    enableReinitialize: true,
    validationSchema: translateSchema('checkoutCart', tCheckout),
    onSubmit: async values => {
      if (!window.grecaptcha) {
        console.error('reCAPTCHA not yet loaded')
        return
      }
      const tokenReCaptcha = await window.grecaptcha.execute(
        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string,
        { action: 'createOrder' }
      )

      if (tokenReCaptcha) {
        if (errorAuth && !errorAuth?.status) {
          setIsProcessing(true)
          const store = listBranch?.find(e => e.code === values.storeCode)
          const data = {
            note: values.note,
            products: cart?.products.map(e => ({
              _id: e.productId,
              quantity: e.quantity,
              services: e.services?.filter(
                item => item?.quantity && item?.quantity > 0
              ),
              giftItems: e.giftItems,
              otherOffers: e.otherOffers,
            })),

            ...(values.storeCode &&
            values.storeCode != '' &&
            values.shippingMethod == SHIPPING_METHOD.PICKUP_IN_STORE
              ? { storeCode: values.storeCode }
              : {}),
            coupon: (couponApply.length > 0 && couponApply) || [],
            paymentMethod: 'cod',
            shippingMethod: values.shippingMethod,
            expectedShippedAt: dayjs(
              `${values.time} ${dayjs(values.date).format('DD/MM/YYYY')}`,
              'HH:mm:ss:SSS DD/MM/YYYY'
            ).valueOf(),
            shippingAddress: {
              name: values.name,
              address:
                values.shippingMethod === SHIPPING_METHOD.PICKUP_IN_STORE &&
                store
                  ? store.street
                  : values.address,
              provinceCode:
                values.shippingMethod === SHIPPING_METHOD.PICKUP_IN_STORE &&
                store
                  ? store.provinceCode
                  : values.provinceCode,
              districtCode:
                values.shippingMethod === SHIPPING_METHOD.PICKUP_IN_STORE &&
                store
                  ? store.districtCode
                  : values.districtCode,
              wardCode:
                values.shippingMethod === SHIPPING_METHOD.PICKUP_IN_STORE &&
                store
                  ? store.wardCode
                  : values.wardCode,
              phone: values.phone,
              gender: values.gender,
              useAddressV2: store ? !!store.useAddressV2 : !!values.useAddressV2,
              ...(values.preference || userAddress
                ? {
                    alternatePhone: values.alternatePhone,
                    alternateName: values.alternateName,
                  }
                : {}),
            },
            invoiceAddress: values.vat
              ? {
                  name: values.invoiceName,
                  address: values.invoiceAddress,
                  phone: values.invoiceTaxNumber,
                  provinceCode: values.provinceCode,
                  districtCode: values.districtCode,
                  wardCode: values.wardCode,
                  taxNumber: values.invoiceTaxNumber,
                  email: values.invoiceEmail,
                }
              : null,
            useBonus: values.useBonus,
            isPreOrder:
              router?.query?.type === AddToCartType.PRE_ORDER ? true : null,
            refPhone: (() => {
              const expiry = Number(localStorage.getItem('refExpiry') || 0)
              if (expiry > Date.now()) return localStorage.getItem('ref') || undefined
              localStorage.removeItem('ref')
              localStorage.removeItem('refExpiry')
            })(),
          } as OrderInput

          const res = await api(`api/order`, {
            method: 'POST',
            baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
            headers: { recaptcha: tokenReCaptcha },
            data: data,
          })

          if (res instanceof Error) {
            setErrorMessage(res.message)
            setErrorCODE((res as any).statusCode)
            if ((res as any).statusCode === 'PRODUCT_OUT_OF_STOCK') {
              setErrorMessage(
                `Một sản phẩm trong giỏ hàng của quý khách đã bán hết. ${systemConfig?.websiteBrandName} sẽ bỏ sản phẩm hết hàng ra khỏi giỏ hàng để tiếp tục đặt hàng`
              )
              setOpenErrorPopup(true)
            }
            if ((res as any).statusCode === 'CART_OUT_OF_DATE') {
              setErrorMessage(
                `Trong giỏ hàng của bạn có một số sản phẩm dịch vụ đã thay đổi. Vui lòng tải lại trang!`
              )
              setOpenErrorPopup(true)
            }
            setIsProcessing(false)
            if (res) return
          }

          if (!user) {
            setGuestInfo({
              ...values,
              saleRegionCode: currentRegion,
            } as GuestInfo)
          }
          pixel.fbqEvent('Purchase', {
            value: cart?.amount,
            currency: 'VND',
            contents: cart?.products,
            content_type: 'product',
          })
          const dataGTM = cart?.products.map(item => ({
            item_id: item.code,
            item_name: getTranslate(item.name),
            discount: item.price - item.finalPrice,
            price: item.price,
            quantity: item.quantity,
          }))
          sendGTMEvent({
            event: 'GA_purchase',
            value: cart.amount,
            currency: 'VND',
            coupon: couponApply,
            item: dataGTM,
          })
          sendGAEvent({
            event: 'GA_purchase',
            value: cart.amount,
            currency: 'VND',
            coupon: couponApply,
            item: dataGTM,
          })
          toast.success(tCheckout('success'), {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: 'light',
          })
          setCouponApply([])
          setVoucherApply([])
          if (isInstallment) {
            const installmentResponse = await api(`api/payment/onepay/link`, {
              method: 'POST',
              baseURL: process.env.NEXT_PUBLIC_FINANCE_API_URL,
              data: {
                method: PAYMENT_METHODS.INSTALLMENT,
                ip: clientIP,
                orderCode: res?.data?.code,
                currency: 'VND',
                fromUrl:
                  window.location.origin +
                  '/checkout/onepay/installment/?orderNumberId=' +
                  res?.data?._id +
                  '&method=' +
                  PAYMENT_METHODS.INSTALLMENT,
                redirectUrl:
                  window.location.origin +
                  '/checkout/onepay/installment/?orderNumberId=' +
                  res?.data?._id +
                  '&method=' +
                  PAYMENT_METHODS.INSTALLMENT,
              },
            })
            if (installmentResponse instanceof Error) {
              toast.error(installmentResponse?.message, {
                position: 'bottom-center',
                autoClose: 1500,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                progress: undefined,
                theme: 'dark',
              })
            } else {
              return router.push(installmentResponse?.data)
            }
          } else {
            return router.push({
              pathname: '/checkout/confirm',
              query: { orderNumberId: res.data._id },
            })
          }
          await setIsProcessing(false)
        }
      }
    },
  })

  useEffect(() => {
    if (
      regionChange &&
      regionChange != currentRegion &&
      !openModalCartNotAvailable
    ) {
      const timeoutId = setTimeout(() => {
        setIsChangeRegionModal(true)
      }, 500)
      return () => clearTimeout(timeoutId)
    }
  }, [regionChange, currentRegion, formik])

  useEffect(() => {
    if (formik.values.useBonus !== useBonusState) {
      setUseBonusState(formik.values.useBonus)
    }
  }, [formik.values.useBonus, useBonusState])

  const titlePageSeo =
    query?.type === PAYMENT_METHODS.INSTALLMENT
      ? 'Trả góp qua OnePay'
      : t('cart')
  useEffect(() => {
    if (
      router?.query?.type &&
      router?.query?.type === AddToCartType.PRE_ORDER &&
      cart?.products?.length > 0 &&
      cart?.products[0] &&
      !cart?.products[0]?.preOrder
    ) {
      setOpenModalCartNotAvailable(true)
    }
  }, [router.query.type, cart?.products])

  const handleRemoveParamsAndReload = () => {
    const remainingParams = { ...router.query }
    delete remainingParams.type
    delete remainingParams.productIds
    router
      .replace(
        {
          pathname: router.pathname,
          query: remainingParams,
        },
        undefined,
        { shallow: true }
      )
      .then(() => {
        router.reload()
      })
  }

  return (
    <Main meta={<Meta title={titlePageSeo} description={titlePageSeo} />}>
      <PageBreadcrumb homeText={tCheckout('home')} breadcrumbs={breadcrumbs} />
      <section className='checkoutCart'>
        {!cart || cart?.products?.length === 0 ? (
          <div className='mt-[5px] flex flex-col items-center justify-center '>
            <div className='mb-3 w-full rounded-lg bg-white'>
              <div className='flex flex-col items-center justify-center py-[12px]'>
                <CheckoutEmpty className='mt-1' />
                <h3 className='mb-4 text-[28px] font-extrabold text-dark-400'>
                  {t('notFound')}{' '}
                </h3>
                <p className='mb-3 text-fieldInput text-dark-400'>
                  {t('continueShopping', {
                    brand: systemConfig?.websiteBrandName,
                  })}
                </p>
                <ButtonLink
                  href='/'
                  text={t('continue')}
                  classes='text-mainBody mx-auto mt-[5px] mb-3 bg-[#DC2626] rounded-full text-white-400 font-bold uppercase rounded-[5px]'
                />
              </div>
            </div>
            <div className='flex flex-col'>
              <div className='container items-start justify-between'>
                <Recommend data={productForYou} />
                <BlogList />
              </div>
            </div>
          </div>
        ) : (
          <div className=''>
            {cartType() == AddToCartType.PRE_ORDER ? (
              <Badge title={t('info')}>
                <CartItems
                  cartLoading={cartLoading}
                  total={
                    cart?.products?.reduce(
                      (acc: number, e: CartItemType) => acc + e?.quantity,
                      0
                    ) || 0
                  }
                  totalPrice={cart?.saleAmount}
                  serviceFee={
                    cart?.serviceFee + cart?.shippingAndInstallFee || 0
                  }
                >
                  {cart?.products && cart?.products.length > 0 ? (
                    cart?.products?.map((e: CartItemType, index) => (
                      <CartItem
                        className={`${
                          cart?.products?.length > 1 &&
                          index < cart?.products.length - 1 &&
                          'border-b border-grey'
                        }`}
                        shippingMethod={formik?.values.shippingMethod}
                        onChange={e => {
                          formik.handleChange({
                            target: {
                              value: e.target.value,
                              name: 'shippingMethod',
                            },
                          })
                        }}
                        handleUpdateCart={changeQuantityItem}
                        key={e.productId}
                        product={e}
                        type={cartType() || AddToCartType.ADD}
                        useBonus={formik.values.useBonus}
                      />
                    ))
                  ) : (
                    <LoadingIndicator />
                  )}
                </CartItems>
              </Badge>
            ) : (
              <div className='flex gap-2'>
                <div className='h-auto flex-[60%]'>
                  <CartItems
                    cartLoading={cartLoading}
                    total={
                      cart?.products?.reduce(
                        (acc: number, e: CartItemType) => acc + e?.quantity,
                        0
                      ) || 0
                    }
                    totalPrice={cart?.saleAmount}
                    serviceFee={
                      cart?.serviceFee + cart?.shippingAndInstallFee || 0
                    }
                  >
                    {cart?.products && cart?.products?.length > 0 ? (
                      cart?.products?.map((e: CartItemType, index) => (
                        <div
                          key={e?.productId}
                          className='mb-2 rounded-2xl bg-white px-2 py-4 shadow-sm'
                        >
                          <CartItem
                            className={`${
                              cart?.products?.length > 1 &&
                              index < cart?.products.length - 1 &&
                              ''
                            }`}
                            onChange={e => {
                              formik.handleChange({
                                target: {
                                  value: e.target.value,
                                  name: 'shippingMethod',
                                },
                              })
                            }}
                            shippingMethod={formik?.values?.shippingMethod}
                            handleUpdateCart={changeQuantityItem}
                            product={e}
                            type={cartType() || AddToCartType.ADD}
                            useBonus={formik.values.useBonus}
                          />
                        </div>
                      ))
                    ) : (
                      <LoadingIndicator />
                    )}
                  </CartItems>
                </div>

                <div className='flex-[40%]'>
                  <DeliveryAddress
                    user={user}
                    provinces={provinces}
                    provinceAvailable={provinceAvailable}
                    districts={districts}
                    wards={wards}
                    storeSelected={storeSelected}
                    storeList={listBranch}
                    setWards={setWards}
                    getDistricts={getDistricts}
                    getWards={getWards}
                    totalPrice={cart?.amount}
                    shippingFee={cart?.shippingFee}
                    shippingAndInstallFee={cart?.shippingAndInstallFee}
                    saleAmount={cart?.saleAmount}
                    discount={cart?.promotionAmount}
                    service={cart?.serviceFee}
                    formik={formik}
                    errorMessage={errorMessage}
                    isSubmitted={isSubmitted}
                    setErrorMessage={setErrorMessage}
                    isProcessing={isProcessing}
                    onSubmitFn={onSubmitFn}
                    address={listAddress}
                    setRefetch={() => setRefetch(true)}
                    coupon={couponApply}
                    preOrderDateTime={
                      cart?.products[0]?.preOrder?.deliveredAfter + bonusDay
                    }
                    conditionDisplayOptionShipping={
                      conditionDisplayOptionShipping
                    }
                    bonusAmount={cart?.bonusAmount || 0}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <ModalConfirmChangeRegion
          isOpen={isChangeRegionModal}
          setIsOpen={() => {
            setIsChangeRegionModal(false)
            setSaleRegion(regionChange)
            window.location.reload()
          }}
        />
        <ModalNotAvailable
          isOpen={openModalCartNotAvailable}
          setIsOpen={() => {
            setOpenModalCartNotAvailable(false)
            handleRemoveParamsAndReload()
          }}
        />
        <ErrorPopup
          errorMessage={errorMessage}
          errorCODE={errorCODE}
          isOpen={openErrorPopup}
          setIsOpen={data => {
            setOpenErrorPopup(data)
            fetchCart()
            setErrorMessage('')
          }}
        />
      </section>
    </Main>
  )
}
export default CheckoutCart
export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req, res, query } = context
  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: context.locale,
    }
  )
  const [
    bottomMenu,
    globalHeaderMenu,
    keywords,
    stores,
    banner,
    regions,
    systemConfig,
    productForYou,
  ] = await Promise.all([
    getBottomMenu(),
    getHeaderMenu(),
    getKeyword(),
    getProvinces(),
    getBannerSSR(),
    getSaleRegion(),
    getConfigSSR(),
    getProductForYou(),
  ])
  return {
    props: {
      bottomMenu,
      globalHeaderMenu,
      keywords,
      stores,
      banner,
      systemConfig,
      language,
      messages,
      regions,
      query,
      token: token || null,
      productForYou,
    },
  }
}
